import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../hooks/use-api-client.js';
import type { SecretsScrub as Batch } from '../api/types.js';
import { SectionAction, SettingsSection } from './settings-section.js';

// The secrets the sessions printed before the mask existed (ADR-043 section 3), on a server's Settings tab: checked
// first, nothing changed, then cleaned on the operator's click. The history goes a batch of sessions per call, the
// transcripts with the first one. Names and kinds of token only: no value reaches the browser.

type Hits = Record<string, number>;
interface Found {
  applied: boolean;
  sessions: NonNullable<Batch['history']>['sessions'];
  total: number;
  transcripts: NonNullable<Batch['transcripts']> | null;
  transcriptsNote?: string;
}

const add = (into: Hits, hits: Hits) => { for (const [k, n] of Object.entries(hits)) into[k] = (into[k] ?? 0) + n; return into; };
const labels = (hits: Hits) => Object.entries(hits).sort((a, b) => b[1] - a[1]).map(([k, n]) => `${k} (${n})`).join(', ');
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function SecretsScrub({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const api = useApiClient();
  const [found, setFound] = useState<Found | null>(null);
  const [progress, setProgress] = useState<{ apply: boolean; read: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!isAdmin) return null;

  const run = async (apply: boolean) => {
    setError(null);
    setProgress({ apply, read: 0, total: 0 });
    const acc: Found = { applied: apply, sessions: [], total: 0, transcripts: null };
    try {
      // The server's transcripts first, a call of their own; then the history, a batch of sessions per call
      const t = await api.servers.scrubSecrets(serverId, { apply, part: 'transcripts' });
      acc.transcripts = t.transcripts ?? null;
      acc.transcriptsNote = t.transcriptsNote;
      let after: string | undefined;
      do {
        const b = await api.servers.scrubSecrets(serverId, { apply, ...(after ? { after } : {}) });
        const h = b.history ?? { sessions: [], read: 0, total: 0 };
        acc.sessions.push(...h.sessions);
        acc.total = h.total;
        setProgress({ apply, read: h.read, total: h.total });
        after = h.next;
      } while (after);
      setFound(acc);
    } catch (e) {
      setError(`${apply ? 'Not cleaned' : 'Not checked'}: ${(e as Error).message}`);
    } finally {
      setProgress(null);
    }
  };

  const busy = !!progress;
  const all: Hits = {};
  for (const s of found?.sessions ?? []) add(all, s.hits);
  for (const t of found?.transcripts?.transcripts ?? []) add(all, t.hits);
  const sessions = found?.sessions.length ?? 0;
  const transcripts = found?.transcripts?.transcripts.length ?? 0;
  const anything = sessions + transcripts > 0;
  const failed = (found?.sessions ?? []).reduce((n, s) => n + (s.failed ?? 0), 0);
  const state = progress
    ? `${progress.apply ? 'Cleaning' : 'Checking'}${progress.total ? ` ${progress.read} of ${plural(progress.total, 'session', 'sessions')}` : ''}…`
    : !found ? 'What the sessions printed before the mask may still hold a secret, in their history and on the server. Check: nothing is changed.'
    : found.applied ? `Cleaned: ${plural(sessions, 'session’s history', 'sessions’ history')} and ${plural(transcripts, 'transcript', 'transcripts')} now show the secrets masked.${failed ? ` ${plural(failed, 'event was', 'events were')} not rewritten: check again.` : ''}`
    : anything ? `Found in ${plural(sessions, 'session’s history', 'sessions’ history')} and ${plural(transcripts, 'transcript', 'transcripts')}: ${labels(all)}.`
    : `Nothing found in the history of ${plural(found.total, 'session', 'sessions')}${found.transcripts ? ' nor in the transcripts' : ''}.`;

  return (
    <SettingsSection id="secrets-scrub-title" title="Secrets kept before the mask" tone={found && ((anything && !found.applied) || failed) ? 'warn' : 'normal'}
      state={state}
      action={<SectionAction onClick={() => void run(false)} disabled={busy}>{found ? 'Check again' : 'Check'}</SectionAction>}>
      {error && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>{error}</p>}
      {found?.transcriptsNote && <p className="mt-1 max-w-[68ch] text-sm" style={{ color: 'var(--color-text-3)' }}>{found.transcriptsNote}.</p>}
      {found && !found.applied && sessions > 0 && (
        <ul className="mt-3 space-y-1 text-sm">
          {found.sessions.map((s) => (
            <li key={s.sessionId} className="min-w-0 break-words">
              <Link to={`/servers/${serverId}/sessions/${s.sessionId}`} className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>{s.name || 'Untitled session'}</Link>
              <span style={{ color: 'var(--color-text-3)' }}> {labels(s.hits)}</span>
            </li>
          ))}
        </ul>
      )}
      {found && !found.applied && (found.transcripts?.inUse.length ?? 0) > 0 && (
        <p className="mt-2 max-w-[68ch] text-sm" style={{ color: 'var(--color-text-3)' }}>
          {plural(found.transcripts!.inUse.length, 'transcript belongs', 'transcripts belong')} to a session running now: cleaned by a later check once it stops.
        </p>
      )}
      {found && (found.transcripts?.files.length ?? 0) > 0 && (
        <div className="mt-3">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>Other files on the server holding one</h3>
          <p className="mt-0.5 max-w-[68ch] text-sm" style={{ color: 'var(--color-text-3)' }}>Left as they are: a tool may need its token there. Rotate the token if the file should not hold it.</p>
          <ul className="mt-1.5 space-y-1 text-sm">
            {found.transcripts!.files.map((f) => (
              <li key={f.path} className="min-w-0 break-all"><span style={{ color: 'var(--color-text-2)' }}>~/{f.path}</span> <span style={{ color: 'var(--color-text-3)' }}>{labels(f.hits)}</span></li>
            ))}
          </ul>
        </div>
      )}
      {found && !found.applied && anything && (
        <button type="button" onClick={() => void run(true)} disabled={busy}
          className="mt-4 rounded-lg px-3 py-1.5 text-sm font-medium focus-visible:outline focus-visible:outline-2 disabled:opacity-60 max-sm:min-h-10"
          style={{ backgroundColor: 'var(--color-brand)', color: 'white' }}>
          Mask them in {plural(sessions, 'session', 'sessions')} and {plural(transcripts, 'transcript', 'transcripts')}
        </button>
      )}
    </SettingsSection>
  );
}
